import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { formatViewCount } from '@repo/utils';

interface YtVideoCardTitleProps {
  title: string;
  viewCount: number | null;
}

export const PureYtVideoCardTitle: React.FC<YtVideoCardTitleProps> = ({
  title,
  viewCount,
}) => {
  const [displayText, setDisplayText] = useState(title);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const truncateTitle = () => {
      const container = containerRef.current;
      const titleElement = titleRef.current;
      if (!container || !titleElement) return;

      const viewsText = ` • ${formatViewCount(viewCount ?? 0)} views`;

      // Get container dimensions
      const containerWidth = container.offsetWidth;
      const style = window.getComputedStyle(titleElement);
      const lineHeight = parseFloat(style.lineHeight);
      const maxHeight = lineHeight * 2; // 4 lines max

      // Create a temporary element to measure text
      const temp = document.createElement('span');
      temp.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-family: ${style.fontFamily};
        font-size: ${style.fontSize};
        font-weight: ${style.fontWeight};
        line-height: ${style.lineHeight};
        white-space: pre-wrap;
        word-break: break-word;
        width: ${containerWidth}px;
      `;
      document.body.appendChild(temp);

      // First check if the full text fits
      temp.textContent = title + viewsText;
      const fullHeight = temp.offsetHeight;

      if (fullHeight <= maxHeight) {
        // Full text fits, no truncation needed
        document.body.removeChild(temp);
        setDisplayText(title);
        setIsTruncated(false);
        return;
      }

      // Binary search for the right truncation point
      let low = 0;
      let high = title.length;
      let bestFit = '';

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const testText = title.substring(0, mid) + '...' + viewsText;

        temp.textContent = testText;
        const height = temp.offsetHeight;

        if (height <= maxHeight) {
          bestFit = title.substring(0, mid) + '...';
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      document.body.removeChild(temp);
      setDisplayText(bestFit);
      setIsTruncated(true);
    };

    truncateTitle();

    const resizeObserver = new ResizeObserver(truncateTitle);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [title, viewCount]);

  const handleTooltipOpenChange = useCallback(
    (open: boolean) => {
      setIsTooltipOpen(open && isTruncated);
    },
    [isTruncated],
  );

  return (
    <div className="flex-1 min-w-0" ref={containerRef}>
      <TooltipProvider>
        <Tooltip
          delayDuration={200}
          open={isTooltipOpen}
          onOpenChange={handleTooltipOpenChange}
        >
          <TooltipTrigger asChild>
            <h3 className="text-black font-semibold text-base leading-tight wrap-break-word cursor-pointer">
              <span ref={titleRef}>
                {displayText}{' '}
                {/* <span className="font-medium text-[#757575] text-sm whitespace-nowrap">
                  • {formatViewCount(viewCount ?? 0)} views
                </span> */}
              </span>
            </h3>
          </TooltipTrigger>
          <TooltipContent className="max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] cred-md:max-w-80 cred-lg:max-w-[330px] cred-xl:max-w-[440px]">
            <p className="text-xs leading-none text-neutral-2 cred-md:text-sm cred-lg:text-base cred-xl:text-base md:text-lg md:leading-[19px]">
              {title}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const YtVideoCardTitle = memo(
  PureYtVideoCardTitle,
  (prev, next) =>
    prev.title === next.title && prev.viewCount === next.viewCount,
);
